import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { AddArtistModal } from '../../../accountSelector/AddArtistModal';
import { useArtistSwitcher } from '../../../accountSelector/useArtistSwitcher';
import { useSwitchWorkspace } from '../../../workspaces/useSwitchWorkspace';
import { WorkspaceSwitcherSection } from '../../../workspaces/WorkspaceSwitcherSection';
import { MobileAppHandoffModal } from '../../../mobileApps/MobileAppHandoffModal';
import { TrialStatusPill } from '../../trialStatus/TrialStatusPill';
import { useAccountIdentity } from '../account/useAccountIdentity';
import { AccountArt } from '../account/AccountArt';
import { AccountMenuList } from '../account/AccountMenuList';
import { AccountWorkspaceName } from '../account/AccountWorkspaceName';
import { accountMenuMessages as messages } from '../account/accountMenuMessages';
import { MobileSheet } from './MobileSheet';
import { MobileAccountArtists } from './MobileAccountArtists';
import styles from './MobileAccountSheet.module.scss';

interface MobileAccountSheetProps {
  id: string;
  /** The social accounts that need a new sign-in: the red count of Connections. */
  accountAttention: number;
  onClose: () => void;
}

/**
 * design: navigation-v2 7d. The account sheet behind the avatar: the art, the name and
 * "email · plan", the trial pill, the workspaces (two or more only) and the artists of the active
 * workspace (design workspaces b1), then the rows of the desktop account menu (6a). "Add artist"
 * and "Get the mobile app" swap the sheet for their window.
 */
const MobileAccountSheetComponent: React.FC<MobileAccountSheetProps> = ({ id, accountAttention, onClose }) => {
  const intl = useIntl();
  const identity = useAccountIdentity();
  const switcher = useArtistSwitcher();
  const { closeAdd } = switcher;
  const workspaceSwitch = useSwitchWorkspace(onClose);
  const [isAppOpen, setAppOpen] = useState(false);
  const openMobileApp = useCallback(() => setAppOpen(true), []);
  const closeAddAndSheet = useCallback(() => {
    closeAdd();
    onClose();
  }, [closeAdd, onClose]);

  if (switcher.isAddOpen) {
    return <AddArtistModal isOpen onClose={closeAddAndSheet} />;
  }
  if (isAppOpen) {
    return <MobileAppHandoffModal isOpen onClose={onClose} />;
  }

  return (
    <MobileSheet
      id={id}
      title={identity.isWorkspace ? <AccountWorkspaceName email={identity.email} /> : identity.name}
      description={intl.formatMessage(messages.sheetLine, { email: identity.email, plan: identity.planLine })}
      leading={
        <AccountArt
          name={identity.name}
          avatarUrl={identity.avatarUrl}
          hasAttention={accountAttention > 0}
          size="sheet"
        />
      }
      onClose={onClose}
    >
      {/* design: onboarding h3-3. The trial pill stays in the sheet; it renders nothing without a trial. */}
      <div className={styles.pills}>
        <TrialStatusPill />
      </div>
      <WorkspaceSwitcherSection variant="sheet" switcher={workspaceSwitch} onClose={onClose} />
      <MobileAccountArtists
        switcher={switcher}
        onClose={onClose}
        isDisabled={workspaceSwitch.pendingLicenseId !== null}
      />
      <div className={styles.menu}>
        <AccountMenuList
          accountAttention={accountAttention}
          onNavigate={onClose}
          onOpenMobileApp={openMobileApp}
          variant="sheet"
        />
      </div>
    </MobileSheet>
  );
};

export const MobileAccountSheet = TypedMemo(MobileAccountSheetComponent);
